import type { DesignPerson, DesignScopeRoot } from "./designUtils";

/**
 * Design fields as held by the create/edit forms. Every field is optional
 * because the create modal submits a four-field subset while the edit page
 * submits the whole form.
 */
export type DesignMutationFormInput = {
  baselineWorkspaceId?: string | null;
  designWorkspaceId?: string | null;
  name?: string | null;
  designType?: string | null;
  sponsors?: DesignPerson[] | null;
  leads?: DesignPerson[] | null;
  participants?: DesignPerson[] | null;
  scopeRoots?: DesignScopeRoot[] | null;
  scopeRootGroupIds?: string[] | null;
  primaryDriver?: string | null;
  purpose?: string | null;
  successCriteria?: string | null;
  benefits?: string | null;
  workflowStage?: string | null;
  startDate?: string | null;
  targetImplementationDate?: string | null;
  baselineDataDate?: string | null;
};

/** Matches the API `DesignInput` accepted by `createDesign` and `updateDesign`. */
export type DesignMutationInput = {
  baselineWorkspaceId?: string | null | undefined;
  designWorkspaceId: string | null;
  name?: string | null | undefined;
  designType?: string | null | undefined;
  sponsorPersonIds?: string[] | undefined;
  leadPersonIds?: string[] | undefined;
  participantPersonIds?: string[] | undefined;
  scopeRootGroupIds?: string[] | null | undefined;
  primaryDriver?: string | null | undefined;
  purpose?: string | null | undefined;
  successCriteria?: string | null | undefined;
  benefits?: string | null | undefined;
  workflowStage?: string | null | undefined;
  startDate: string | null;
  targetImplementationDate: string | null;
  baselineDataDate: string | null;
};

export const personIdsForDesignInput = (
  people?: (DesignPerson | null | undefined)[] | null
): string[] =>
  (people || [])
    .map((p) => p?.aggregateId)
    .filter((aggregateId): aggregateId is string => Boolean(aggregateId));

const optionalDate = (dateValue?: string | null): string | null =>
  dateValue || null;

export const buildDesignMutationInput = (
  input: DesignMutationFormInput
): DesignMutationInput => ({
  baselineWorkspaceId: input.baselineWorkspaceId,
  designWorkspaceId: input.designWorkspaceId || null,
  name: input.name,
  designType: input.designType,
  sponsorPersonIds: input.sponsors
    ? personIdsForDesignInput(input.sponsors)
    : undefined,
  leadPersonIds: input.leads ? personIdsForDesignInput(input.leads) : undefined,
  participantPersonIds: input.participants
    ? personIdsForDesignInput(input.participants)
    : undefined,
  scopeRootGroupIds: input.scopeRoots
    ? input.scopeRoots.map((r) => r.groupId)
    : input.scopeRootGroupIds,
  primaryDriver: input.primaryDriver,
  purpose: input.purpose,
  successCriteria: input.successCriteria,
  benefits: input.benefits,
  workflowStage: input.workflowStage,
  startDate: optionalDate(input.startDate),
  targetImplementationDate: optionalDate(input.targetImplementationDate),
  baselineDataDate: optionalDate(input.baselineDataDate),
});
